import {
  $convertToMarkdownString,
  $generateNodesFromMarkdownString,
  CHECK_LIST,
  TEXT_MATCH_TRANSFORMERS,
  type Transformer,
} from "@lexical/markdown";
import {
  $getRoot,
  $getSelection,
  $setSelection,
  createEditor,
  ElementNode,
  type Klass,
  type LexicalNode,
  type SerializedEditorState,
} from "lexical";
import { marked } from "marked";
import type { TDocumentDefinitions } from "pdfmake/interfaces";
import tailwindColors from "tailwindcss/colors";

import { getPropertyDisplayTitle } from "~/common/propertyDisplay";
import { PropertyKind } from "~/shared/enums";
import type { Doc, Page, Property, PropertyConfig, Task } from "~/shared/types";
import { saveFile } from "~/utils/api";
import { toHex } from "~/utils/color";
import { escapeHtml } from "~/utils/common";
import { printOrCaptureMessage } from "~/utils/exceptionClient";
import { isImageType } from "~/utils/media";

import {
  BOLD_ITALIC_STAR_TRIPLE,
  BOLD_ITALIC_UNDERSCORE_TRIPLE,
  BOLD_STAR_DOUBLE,
  BOLD_UNDERSCORE_DOUBLE,
  CODE_BACKTICK,
  CODE_BLOCK,
  DART_LINK,
  ENTITY,
  HEADING,
  HIGHLIGHT_EQUALS,
  IMAGE,
  ITALICS_STAR,
  ITALICS_UNDERSCORE,
  LINE_BREAK,
  ORDERED_LIST,
  QUOTE,
  STRIKE_THROUGH_TILDE,
  TAB,
  TABLE,
  UNDERLINE,
  UNORDERED_LIST,
} from "./markdownRules";
import { HORIZONTAL_RULE_TRANSFORMER } from "./nodes/HorizontalRuleNode";

const FAVICON_FILE = window?.location?.hostname === "app.dartai.com" ? "favicon.png" : "favicon_dev.png";
const HTML_EXPORT_COLORS = {
  bodyText: toHex(tailwindColors.gray["900"]),
  border: toHex(tailwindColors.gray["200"]),
  headerBackground: toHex(tailwindColors.gray["50"]),
  headerText: toHex(tailwindColors.gray["700"]),
  tableText: toHex(tailwindColors.gray["600"]),
  tableCellBorder: toHex(tailwindColors.gray["100"]),
};
const htmlExportTemplate = (title: string, content: string) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">

  <title>${title}</title>
  <meta
    name="description"
    content="Dart is the ultimate AI project management tool. It will save you seven hours per week in project management overhead." />

  <link rel="icon" sizes="256x256" href="https://app.dartai.com/static/img/${FAVICON_FILE}" />

  <link rel="stylesheet" href="https://rsms.me/inter/inter.css" />
  <style>
    body {
      font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
      line-height: 1.6;
      color: ${HTML_EXPORT_COLORS.bodyText};
      max-width: 1200px;
      margin: 0 auto;
      padding: 1rem;
    }

    h1 {
      font-size: 2rem;
      margin-bottom: 1.5rem;
    }
    h2 {
      font-size: 1.5rem;
      margin: 2rem 0 1rem;
    }
    p {
      margin: 1rem 0;
    }

    hr {
      border: none;
      border-top: 1px solid ${HTML_EXPORT_COLORS.border};
      margin: 2rem 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1.5rem 0;
      box-shadow: none;
      border-bottom: 1px solid ${HTML_EXPORT_COLORS.border};
    }
    th, td {
      text-align: left;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid ${HTML_EXPORT_COLORS.border};
    }
    thead {
      background-color: ${HTML_EXPORT_COLORS.headerBackground};
    }
    th {
      font-weight: 600;
      font-size: 0.875rem;
      color: ${HTML_EXPORT_COLORS.headerText};
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    td {
      font-size: 0.95rem;
      color: ${HTML_EXPORT_COLORS.tableText};
      border-color: ${HTML_EXPORT_COLORS.tableCellBorder};
    }
    tbody tr:last-child td {
      border-bottom: none;
    }

    @media print {
      body {
        max-width: 100%;
      }
    }
  </style>
</head>
<body>
${content}
</body>
</html>`;

type GetShownPropertyWithConfigList = (
  alwaysIncludeKinds?: PropertyKind[],
  alwaysIncludeDuids?: string[]
) => [Property, PropertyConfig][];

const WIDE_PROPERTY_KINDS = [PropertyKind.DEFAULT_TITLE, PropertyKind.DEFAULT_DESCRIPTION];
const WIDE_MULTIPLIER = 3;

// code should go first as it prevents any transformations inside
// then longer tags match (e.g. ** or __ should go before * or _)
export const TRANSFORMERS: Transformer[] = [
  CODE_BLOCK,
  // element
  HEADING,
  QUOTE,
  CHECK_LIST,
  ORDERED_LIST,
  UNORDERED_LIST,
  TABLE,
  // TODO toggle block add back
  // TOGGLE_BLOCK_TRANSFORMER,
  HORIZONTAL_RULE_TRANSFORMER,
  IMAGE,
  TAB,
  LINE_BREAK,
  // text format
  BOLD_ITALIC_STAR_TRIPLE,
  BOLD_ITALIC_UNDERSCORE_TRIPLE,
  BOLD_STAR_DOUBLE,
  BOLD_UNDERSCORE_DOUBLE,
  ITALICS_STAR,
  ITALICS_UNDERSCORE,
  STRIKE_THROUGH_TILDE,
  CODE_BACKTICK,
  HIGHLIGHT_EQUALS,
  UNDERLINE,
  ENTITY,
  DART_LINK,
  // link only
  ...TEXT_MATCH_TRANSFORMERS,
];

let FULL_DART_EDITOR_NODES: Klass<LexicalNode>[] = [];

export const init = (fullDartEditorNodes: Klass<LexicalNode>[]) => {
  FULL_DART_EDITOR_NODES = fullDartEditorNodes;
};

export const fillNodeWithMarkdown = (node: ElementNode, markdown: string) => {
  const selection = $getSelection()?.clone() ?? null;
  const children = $generateNodesFromMarkdownString(markdown, TRANSFORMERS);
  $setSelection(selection);
  node.clear().append(...children);
};

const escapeCsvField = (field: string) => {
  let res = field.replaceAll('"', '""');
  if (res.includes(",") || res.includes("\n") || res.includes('"')) {
    res = `"${res}"`;
  }
  return res;
};

export const convertTableToCsv = (table: string[][]) =>
  table.map((row) => row.map(escapeCsvField).join(",")).join("\n");

export const convertToMarkdown = (text: SerializedEditorState) => {
  if (text.root.children.length === 0) {
    return "";
  }
  const editor = createEditor({ nodes: FULL_DART_EDITOR_NODES });
  editor.setEditorState(editor.parseEditorState(text));

  let res = "";
  editor.update(() => {
    res = $convertToMarkdownString(TRANSFORMERS, $getRoot());
  });
  return res;
};

const formatAttachmentsSection = (attachments: { name: string; fileUrl: string; kind: string }[]) => {
  if (attachments.length === 0) {
    return "";
  }

  // TODO exposing the fact that we have security by obscurity for attachments
  const attachmentsList = attachments
    .map((attachment) => {
      if (isImageType(attachment.kind)) {
        return `<img src="${attachment.fileUrl}" alt="${attachment.name}" style="max-width: 100%; height: auto; display: inline-block;" />`;
      }
      return `- [${attachment.name}](${attachment.fileUrl})`;
    })
    .join("\n\n");

  return `\n\n## Attachments\n\n${attachmentsList}`;
};

const formatPropertiesTable = (task: Task, getShownPropertyWithConfigList: GetShownPropertyWithConfigList) =>
  getShownPropertyWithConfigList()
    .map(([property, config]) => `| ${getPropertyDisplayTitle(property)} | ${config.getValuePretty(property, task)} |`)
    .join("\n");

export const convertTaskToMarkdown = async (
  task: Task,
  getShownPropertyWithConfigList: GetShownPropertyWithConfigList,
  dataStore: {
    getAttachmentsByDuidsOrdered: (duids: string[]) => Promise<{ name: string; fileUrl: string; kind: string }[]>;
  },
  hideProperties: boolean = false
) => {
  const propertiesTableMaybe = hideProperties
    ? ""
    : `| Property | Value |\n| - | - |\n${formatPropertiesTable(task, getShownPropertyWithConfigList)}\n\n`;
  const attachmentsSection = formatAttachmentsSection(
    await dataStore.getAttachmentsByDuidsOrdered(task.attachmentDuids)
  );
  return `# ${task.title}\n\n${propertiesTableMaybe}${convertToMarkdown(task.description)}${attachmentsSection}`;
};

export const convertDocToMarkdown = (doc: Doc) => `# ${doc.title}\n\n${convertToMarkdown(doc.text)}`;

export const convertToPlainText = (markdownStr: string) => {
  const htmlString = marked.parse(markdownStr) as string;
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.body.textContent ?? "";
};

export const convertPageToTable = (tasks: Task[], getShownPropertyWithConfigList: GetShownPropertyWithConfigList) => {
  const properties = getShownPropertyWithConfigList([PropertyKind.DEFAULT_TITLE, PropertyKind.DEFAULT_DESCRIPTION]);
  const headers = properties.map(([property]) => getPropertyDisplayTitle(property));
  const rows = tasks.map((task) =>
    properties.map(([property, config]) => {
      const value = config.getValuePretty(property, task);
      if (property.kind === PropertyKind.DEFAULT_DESCRIPTION) {
        return convertToPlainText(value);
      }
      return value;
    })
  );
  return [headers, ...rows];
};

export const convertPageToHtmlTable = (
  tasks: Task[],
  getShownPropertyWithConfigList: GetShownPropertyWithConfigList
) => {
  const properties = getShownPropertyWithConfigList(WIDE_PROPERTY_KINDS);

  const total =
    properties.length +
    (WIDE_MULTIPLIER - 1) * properties.filter(([property]) => WIDE_PROPERTY_KINDS.includes(property.kind)).length;
  const wideWidth = (100 * WIDE_MULTIPLIER) / total;
  const normalWidth = 100 / total;

  const headers = properties.map(([property]) => ({
    title: getPropertyDisplayTitle(property),
    width: WIDE_PROPERTY_KINDS.includes(property.kind) ? wideWidth : normalWidth,
  }));

  const rows = tasks.map((task) =>
    properties.map(([property, config]) => {
      const value = config.getValuePretty(property, task);
      if (property.kind === PropertyKind.DEFAULT_DESCRIPTION) {
        return value;
      }
      return escapeHtml(value);
    })
  );

  const headerRow = `<tr>${headers.map((h) => `<th style="width: ${h.width}%;">${h.title}</th>`).join("")}</tr>`;
  const bodyRows = rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("\n");

  return `<table>
<thead>
${headerRow}
</thead>
<tbody>
${bodyRows}
</tbody>
</table>`;
};

export const convertPageToHtml = (
  page: Page,
  groupedTasks: { title: string; colorHex?: string; tasks: Task[] }[],
  getShownPropertyWithConfigList: GetShownPropertyWithConfigList,
  spaceName?: string
) => {
  const tasksHtml = groupedTasks
    .map(({ title, colorHex, tasks }) => {
      const htmlTable = convertPageToHtmlTable(tasks, getShownPropertyWithConfigList);
      if (title) {
        if (colorHex) {
          return `<h2 style="background-color: ${colorHex}33; padding: 4px 24px; border-radius: 4px;">${title}</h2>\n${htmlTable}`;
        }
        return `<h2>${title}</h2>\n${htmlTable}`;
      }
      return htmlTable;
    })
    .join("\n\n");

  const pageTitle = spaceName ? `${spaceName} / ${page.title}` : page.title;
  return `<h1>${pageTitle}</h1>\n<hr>\n${tasksHtml}`;
};

export const convertMarkdownToHtml = (markdownStr: string) => {
  const preprocessed = markdownStr.replace(/(\n\s*){3,}/g, (match) => {
    const newlineCount = (match.match(/\n/g) || []).length;
    const extraBrs = "<br>\n".repeat(newlineCount - 1);
    return `\n\n${extraBrs}\n`;
  });
  return marked.parse(preprocessed) as string;
};

const constrainImageWidths = (node: unknown): void => {
  if (Array.isArray(node)) {
    node.forEach(constrainImageWidths);
  } else if (node && typeof node === "object") {
    const obj = node as Record<string, unknown>;
    if ("image" in obj) {
      // Use page width minus margins (approx 515 points for A4 with standard margins)
      obj.fit = [515, 10000];
    }
    Object.values(obj).forEach((value) => {
      if (typeof value === "object") {
        constrainImageWidths(value);
      }
    });
  }
};

export const saveMarkdownAsPdf = async (
  markdownStr: string,
  fileSlug: string,
  options?: { postprocessHtml?: (html: string) => string; pdfOptions?: Omit<TDocumentDefinitions, "content"> }
) => {
  const { postprocessHtml, pdfOptions } = options ?? {};
  let html = convertMarkdownToHtml(markdownStr);
  if (postprocessHtml) {
    html = postprocessHtml(html);
  }

  // Convert image URLs to data URLs
  const imageUrls = html.match(/src="([^"]+)"/g)?.map((src) => src.slice(5, -1)) ?? [];
  const imageDataUrls: Record<string, string> = {};

  await Promise.all(
    imageUrls.map(async (url) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        imageDataUrls[url] = dataUrl;
      } catch {
        printOrCaptureMessage("Failed to load image while saving as PDF", { url });
      }
    })
  );

  // Replace image URLs with data URLs in HTML before htmlToPdfmake processes it
  Object.entries(imageDataUrls).forEach(([url, dataUrl]) => {
    html = html.replaceAll(url, dataUrl);
  });

  const [{ default: htmlToPdfmake }, { default: pdfMake }, { default: fonts }] = await Promise.all([
    import("html-to-pdfmake"),
    import("pdfmake/build/pdfmake"),
    import("~/constants/font"),
  ]);
  pdfMake.addVirtualFileSystem(fonts);
  const content = htmlToPdfmake(html, { tableAutoSize: true });
  constrainImageWidths(content);

  const pdf = pdfMake.createPdf({ content, ...pdfOptions });
  pdf.download(`${fileSlug}.pdf`);
};

export const saveCsv = (csvStr: string, fileSlug: string) => {
  saveFile(new Blob([csvStr], { type: "text/csv" }), `${fileSlug}.csv`);
};

export const saveMarkdownAsHtml = (
  markdownStr: string,
  fileSlug: string,
  options?: { postprocessHtml?: (html: string) => string }
) => {
  const { postprocessHtml } = options ?? {};
  let html = convertMarkdownToHtml(markdownStr);
  if (postprocessHtml) {
    html = postprocessHtml(html);
  }

  const styledHtml = htmlExportTemplate(fileSlug, html);
  saveFile(new Blob([styledHtml], { type: "text/html" }), `${fileSlug}.html`);
};

export const saveHtml = (htmlContent: string, fileSlug: string) => {
  const styledHtml = htmlExportTemplate(fileSlug, htmlContent);
  saveFile(new Blob([styledHtml], { type: "text/html" }), `${fileSlug}.html`);
};

export const saveMarkdown = (markdownStr: string, fileSlug: string) => {
  saveFile(new Blob([markdownStr], { type: "text/plain" }), `${fileSlug}.md`);
};

export const getExportNewWindowScriptContent = (x: string, print: boolean, width: number, height: number) => {
  if (print) {
    return `<script>
const run = async () => {
  window.print();
  window.close();
};

window.onload = run;
</script>`;
  }

  return `<script src="https://cdnjs.cloudflare.com/ajax/libs/html-to-image/1.11.13/html-to-image.min.js" integrity="sha512-iZ2ORl595Wx6miw+GuadDet4WQbdSWS3JLMoNfY8cRGoEFy6oT3G9IbcrBeL6AfkgpA51ETt/faX6yLV+/gFJg==" crossorigin="anonymous" referrerpolicy="no-referrer"></script>
<script>
const run = async () => {
  const content = document.body.children[0];

  const dataUrl = await htmlToImage.toPng(content, {
    width: ${width},
    height: ${height},
  });
  const link = document.createElement("a");
  link.download = "${x}.png";
  link.href = dataUrl;
  link.click();
  link.remove();

  document.getElementsByClassName("dart-message")[0].innerHTML = "After the file downloads, close this window";
};

window.onload = run;
</script>`;
};
